import { LitElement, html, css } from 'lit'
import { store } from '../store.js'
import { api } from '../api.js'
import { navigate } from '../router.js'
import * as lookups from '../lookups.js'
import { toast } from './app-toast.js'

class PageOnboarding extends LitElement {
  static properties = {
    _integrations: { state: true },
    _credFor: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    h1 { color: var(--zg-brand); }
    p.lead { color: var(--zg-muted); margin-top: 0; }
    .card { background: var(--zg-surface); border: 1px solid var(--zg-border);
            border-radius: var(--zg-radius); box-shadow: var(--zg-shadow);
            padding: 1rem 1.25rem; margin-bottom: 0.75rem; }
    .row { display: flex; align-items: center; gap: 1rem; }
    .row .info { flex: 1; }
    .name { font-weight: 600; }
    .desc { color: var(--zg-muted); font-size: 0.875rem; }
    .status { font-size: 0.8rem; padding: 0.15rem 0.55rem; border-radius: 999px; text-transform: capitalize; }
    .status.connected { background: #e6f4ea; color: #137333; }
    .status.skipped { background: #eee; color: #666; }
    .status.disconnected { background: #fdeaea; color: #a61b1b; }
    .status.error { background: #fdeaea; color: #a61b1b; }
    button { font: inherit; cursor: pointer; border-radius: 6px; border: 1px solid var(--zg-border);
             padding: 0.45rem 0.9rem; background: var(--zg-surface); }
    button.primary { background: var(--zg-accent); color: var(--zg-accent-ink); border-color: var(--zg-accent); }
    button:disabled { opacity: 0.5; cursor: default; }
    .creds { display: flex; gap: 0.5rem; margin-top: 0.75rem; }
    .creds input { flex: 1; padding: 0.45rem; border: 1px solid var(--zg-border); border-radius: 6px; }
    .mandatory { color: #a61b1b; font-size: 0.75rem; }
    .done { margin-top: 1rem; }
  `

  connectedCallback() {
    super.connectedCallback()
    this._integrations = store.get().integrations || []
    this._refresh()
  }

  async _refresh() {
    const [integrations, user] = await Promise.all([
      api.get('/integrations'),
      api.get('/users/me')
    ])
    this._integrations = integrations
    store.set({ integrations, user })
    if (user.onboardingComplete) {
      // Anything fetched before this point was fetched as a 'pending' user, whose
      // requests the Workamajig routes reject (requireActive → 403) and whose
      // integration wasn't connected yet (409). Clear those attempts so the day
      // page starts from a clean slate — navigate() below is pushState, so
      // nothing else resets this module.
      lookups.reset()
      toast('You\'re all set!', 'success')
      navigate('/')
    }
  }

  async _connect(p) {
    if (p.connect === 'credentials') {
      this._credFor = this._credFor === p.provider ? null : p.provider
      return
    }
    this._busy = p.provider
    try {
      const { authUrl } = await api.get(`/integrations/${p.provider}/connect`)
      window.location.href = authUrl
    } catch (err) {
      toast(err.message, 'error')
      this._busy = null
    }
  }

  async _submitCreds(e, p) {
    e.preventDefault()
    const fd = new FormData(e.target)
    this._busy = p.provider
    try {
      await api.post(`/integrations/${p.provider}/connect`, {
        accessToken: fd.get('accessToken'),
        externalAccountId: fd.get('externalAccountId') || undefined
      })
      this._credFor = null
      await this._refresh()
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      this._busy = null
    }
  }

  async _skip(p) {
    this._busy = p.provider
    try {
      await api.post(`/integrations/${p.provider}/skip`)
      await this._refresh()
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      this._busy = null
    }
  }

  render() {
    const items = this._integrations || []
    return html`
      <h1>Connect your tools</h1>
      <p class="lead">Ziggy reviews your activity in these tools to find time you may have missed.
        Connect the ones you use — you can skip the rest.</p>

      ${items.map(p => html`
        <div class="card">
          <div class="row">
            <div class="info">
              <div class="name">${p.label}
                ${p.mandatory ? html`<span class="mandatory">· required</span>` : ''}</div>
              <div class="desc">${p.description}</div>
            </div>
            <span class="status ${p.status}">${p.status}</span>
            ${p.status === 'connected'
              ? html`<button disabled>Connected</button>`
              : html`
                <button class="primary" ?disabled=${this._busy === p.provider}
                  @click=${() => this._connect(p)}>Connect</button>
                ${p.mandatory ? '' : html`
                  <button ?disabled=${this._busy === p.provider}
                    @click=${() => this._skip(p)}>Skip</button>`}
              `}
          </div>
          ${this._credFor === p.provider ? html`
            <form class="creds" @submit=${(e) => this._submitCreds(e, p)}>
              <input name="accessToken" placeholder="${p.label} API token" required />
              <input name="externalAccountId" placeholder="Account / user id (optional)" />
              <button class="primary" type="submit" ?disabled=${this._busy === p.provider}>Save</button>
            </form>` : ''}
        </div>
      `)}
    `
  }
}

customElements.define('page-onboarding', PageOnboarding)
